import Route from '@ember/routing/route';
import { service } from '@ember/service';
import type SessionContextService from 'tio-employee/services/session-context';
import type PreRegisterService from 'tio-employee/services/pre-register';
import type SessionService from 'tio-employee/services/session';
import type RouterService from '@ember/routing/router-service';
import type CableService from 'tio-common/services/cable';

const SAML_METADATA_NS = 'urn:oasis:names:tc:SAML:2.0:metadata';

export default class LogoutRoute extends Route {
  @service declare sessionContext: SessionContextService;
  @service declare preRegister: PreRegisterService;
  @service declare session: SessionService;
  @service declare router: RouterService;
  @service declare cable: CableService;

  async beforeModel() {
    // Capture SLO URL before clearing session state, since preRegister settings
    // are available pre-auth and won't be affected by logout.
    const sloUrl = await this.#getSloUrl();

    this.cable.disconnect();

    // Prevent handleInvalidation from firing a competing transition to /logout
    // by flagging the session, then performing a full logout.
    this.session.setTransientValue('isLoggingOut', true);
    await this.sessionContext.logout();

    if (sloUrl) {
      window.location.href = sloUrl;
    } else {
      this.router.transitionTo('/logged-out');
    }
  }

  /**
   * Fetches the SP metadata XML from the SAML metadata endpoint and parses
   * the SingleLogoutService Location from it. Returns the URL if found, or
   * undefined if the company is not SSO or the metadata has no SLO service.
   */
  async #getSloUrl(): Promise<string | undefined> {
    const ssoAuthUrl = this.preRegister.settings?.ssoAuthUrl;

    if (!ssoAuthUrl) {
      return undefined;
    }

    const metaUrl = ssoAuthUrl.replace(/\/auth$/, '/meta');

    try {
      const response = await fetch(metaUrl, { signal: AbortSignal.timeout(5000) });

      if (!response.ok) {
        return undefined;
      }

      const xmlText = await response.text();
      const parser = new DOMParser();
      const xml = parser.parseFromString(xmlText, 'application/xml');
      const sloElement = xml.getElementsByTagNameNS(SAML_METADATA_NS, 'SingleLogoutService')[0];

      return sloElement?.getAttribute('Location') || undefined;
    } catch (e) {
      console.error('[route:logout] Failed to fetch SLO URL from metadata', e);
      return undefined;
    }
  }
}
